import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  ELocale,
  LOCALE_STORAGE_KEY,
  isSupportedLocale,
  SUPPORTED_LOCALES,
} from '@/types/ELocale'
import { i18n } from '@/plugins/i18n'

function persistLocale(locale: ELocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // localStorage may be unavailable
  }
}

function applyLocaleToI18n(locale: ELocale): void {
  ;(i18n.global.locale as unknown as { value: string }).value = locale
}

const availableLocales: ReadonlyArray<{ value: ELocale; label: string }> = [
  { value: ELocale.RU, label: 'Русский' },
  { value: ELocale.EN, label: 'English' },
]

export function useLocale() {
  let localeRef: { value: string }
  try {
    const { locale } = useI18n()
    localeRef = locale as unknown as { value: string }
  } catch {
    localeRef = i18n.global.locale as unknown as { value: string }
  }

  const currentLocale = computed<ELocale>(() => {
    return isSupportedLocale(localeRef.value) ? localeRef.value : ELocale.RU
  })

  function setLocale(next: ELocale): void {
    if (!SUPPORTED_LOCALES.includes(next)) return
    persistLocale(next)
    applyLocaleToI18n(next)
  }

  function resolveInitialLocale(languageCode?: string | null): void {
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
      if (isSupportedLocale(stored)) {
        applyLocaleToI18n(stored)
        return
      }
    } catch {
      // ignore
    }

    if (isSupportedLocale(languageCode)) {
      setLocale(languageCode)
      return
    }
  }

  return {
    currentLocale,
    setLocale,
    availableLocales,
    resolveInitialLocale,
  }
}
